import { Injectable } from '@angular/core';
import { Observable, of as observableOf, throwError } from 'rxjs';
import { ApiResourceService } from '../../apiResource/apiResource.service';
import { IGetAirTripQuote, IGetAirTripQuotes } from "./IGetAirTripQuotes";
import { tap } from 'rxjs/operators';
import { IAirQuoteRequest, IAirQuoteResponse } from './IAirQuote.interface';
import { wenji } from 'libraries/interfaces/IWenji';
import { LanguageService } from "libraries/service/language/language.service";
import { PriceSwitcherService } from "libraries/component/tab-navigation/price-switcher.service";
import {
    EPriceRetailXRole,
    IPriceRetailItem,
    IPostAirSearchRequest,
    ISegment,
    ISegmentBeginBetween,
    ISegmentBeginBetweenMinMax,
    ISegmentBeginIntervalle,
    ISegmentBeginValue, IPostAirSearchRequestTraveller
} from "libraries/interfaces/i-post-air-search-request";
import * as moment from "moment";
import { FormHelperService } from "libraries/service/form/form-helper.service";
import _cloneDeep from "lodash-es/cloneDeep";
import { Yii } from "libraries/interfaces/Yii";

const AIR: string = "air";
const TRIPS: string = "trips";
const SEARCH: string = "search";
const QUOTES: string = "quotes";
const QUOTE: string = "quote";

@Injectable()
export class AirTripResourceService {
    private readonly cache: Map<string, IGetAirTripQuotes> = new Map();

    constructor(
        private readonly apiResourceService: ApiResourceService,
        private readonly languageService: LanguageService,
        private readonly priceSwitcherService: PriceSwitcherService,
        private readonly formHelperService: FormHelperService,
    ) {
    }

    public search(body: IPostAirSearchRequest): Observable<IGetAirTripQuotes> {
        const languageCode: string = this.languageService.getUserLanguage();

        let request: IPostAirSearchRequest = this.formHelperService.removeEmptyObjectsAndFormatDate(body, [], true);

        let message: string = Yii.t('wui-iqba-js', 'Une erreur est survenue');
        let isCorrectRequest: boolean = true;

        request = {
            ...request,
            segment: body.segment.map((segmentItem: ISegment): ISegment => {

                let begin: any = segmentItem.begin;
                let end: any = segmentItem.end;
                // @ts-ignore
                let duration: any = segmentItem.duration;
                if (typeof begin === 'string') {
                    begin = begin.split('T')[0];
                }
                if (begin.between?.value && begin.between.value instanceof Date) {
                    begin.between.value = moment(begin as Date).format('YYYY-MM-DD');
                }
                if (begin instanceof Date) {
                    begin = moment(begin as Date).format('YYYY-MM-DD');
                }

                if (typeof begin === 'object' && begin?.between) {
                    begin = this.correctBeginTimes(begin);
                }
                if (typeof end === 'object' && end?.between) {
                    end = this.correctBeginTimes(end);
                }

                if (duration?.max) {
                    segmentItem.viaArray = [{
                        duration: {
                            xUnit: duration.unit,
                            max: duration.max,
                        }
                    }];
                    // @ts-ignore
                    delete segmentItem.duration;
                } else if (duration) {
                    // @ts-ignore
                    delete segmentItem.duration;
                }

                return {
                    ...segmentItem,
                    begin,
                    end
                };
            }),
        };

        if (request.price?.retailArray?.length) {
            const retailForTransportation: IPriceRetailItem = request.price.retailArray
                .find((item: IPriceRetailItem): boolean => item.xRole === EPriceRetailXRole.Transportation);

            const roles: EPriceRetailXRole[] = [EPriceRetailXRole.Service];

            if (retailForTransportation) {

                // add missing xRole in retailArray
                roles.forEach((xRole: EPriceRetailXRole): void => {
                    const isRoleExist: boolean = Boolean(request.price.retailArray
                        .find((item: IPriceRetailItem): boolean => item.xRole === xRole));
                    if (!isRoleExist) {
                        request.price.retailArray.push(
                            {
                                ...retailForTransportation,
                                xRole,
                            }
                        );
                    }
                });
            } else {
                isCorrectRequest = false;
                message = Yii.t('wui-iqba-js', 'Les instructions de calcul du prix de vente pour "Transportation" sont manquantes.');
            }
        }

        // request = this.formHelperService.removeEmpty(body, [], false);

        if (request.traveller?.length) {
            request.quantity = {
                adult : this.computeTravellerQuantityByType(request.traveller, 'adult'),
                child: this.computeTravellerQuantityByType(request.traveller, 'child'),
                infant: this.computeTravellerQuantityByType(request.traveller, 'infant')
            };
        }

        // @ts-ignore
        this.priceSwitcherService.showPriceSwitcher(Boolean(request?.price?.retail || request?.price?.retailArray?.length));

        if (isCorrectRequest) {
            return this.apiResourceService.postOne<IPostAirSearchRequest, IGetAirTripQuotes>(
                this.apiResourceService.getApiUrl(AIR, TRIPS, SEARCH),
                {
                    ...request,
                    languageCode,
                },
                {},
                {target: wenji.ui.target}
            );
        }
        throw new Error(message);


    }

    public getTripQuotes(tripId: string): Observable<IGetAirTripQuotes> {
        if (this.cache && this.cache.has(tripId)) {
            return observableOf(this.cache.get(tripId));
        }
        return this.apiResourceService.getOne<IGetAirTripQuotes>(
            this.apiResourceService.getApiUrl(AIR, TRIPS, tripId, QUOTES)
        ).pipe(
            tap((response: IGetAirTripQuotes): void => {
                this.cache.set(tripId, response);
            })
        );
    }

    public getQuote(tripId: string, quoteId: string): Observable<IGetAirTripQuote> {
        if (this.cache && this.cache.has(tripId + quoteId)) {
            return observableOf(this.cache.get(tripId + quoteId));
        }
        return this.apiResourceService.getOne<IGetAirTripQuotes>(
            this.apiResourceService.getApiUrl(AIR, TRIPS, tripId, QUOTES, quoteId),
        ).pipe(
            tap((response: IGetAirTripQuotes) => {
                this.cache.set(tripId + quoteId, response);
            })
        );
    }

    public quote(body: IAirQuoteRequest): Observable<IAirQuoteResponse> {
        return this.apiResourceService.postOne<IAirQuoteRequest, IAirQuoteResponse>(
            this.apiResourceService.getApiUrl(AIR, QUOTES, QUOTE),
            body,
            {},
            {target: wenji.ui.target}
        );
    }

    private correctBeginTimes(begin: ISegmentBeginValue): ISegmentBeginValue {
        if (begin?.between?.min || begin?.between?.max) {
            const min: ISegmentBeginBetweenMinMax = begin?.between?.min?.time ?
                {
                    time: begin.between.min.time.length === 5 ? `${begin.between.min.time}:00` : begin.between.min.time
                } : null;
            const max: ISegmentBeginBetweenMinMax = begin?.between?.max?.time ?
                {
                    time: begin.between.max.time.length === 5 ? `${begin.between.max.time}:00` : begin.between.max.time
                } : null;
            const between: ISegmentBeginBetween = min && max ? {
                    min,
                    max
                } :
                min && !max ?
                    {min} :
                    {max};
            return {
                ...begin,
                between
            };
        }
        return begin;
    }

    private computeTravellerQuantityByType(travellers: IPostAirSearchRequestTraveller[], type: string): number{
        return travellers
            .filter((travellerItem: IPostAirSearchRequestTraveller): boolean => travellerItem.type.toLowerCase() === type)
            .length;
    }
}
